import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {DateParam, NumberParam, StringParam, useQueryParams, withDefault} from 'use-query-params';
import moment from 'moment';
import {Box, Button, Grid, MenuItem, Stack, TextField} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';

import {RealizationStatuses} from '@/types';
import {useGetRealizationsQuery} from '@/services';
import {AppBar, DebouncedTextField, Empty, Error, Loader} from '@/components';
import {CheckFlat, TimesFlat} from '@/assets/icons';
import {OrderDirections, ORDER_DIRECTIONS} from '@/utils/order-directions';

import {RealizationsTable, CreateRealization} from './components';
import {useLazyGetRealizationsQuery} from "@/services/realization";

export const REALIZATION_STATUSES = [
  {
    label: 'Проведён',
    value: RealizationStatuses.CARRIED_OUT,
    icon: <CheckFlat sx={{fontSize: 16}} color="success"/>,
  },
  {label: 'Отменён', value: RealizationStatuses.CANCELLED, icon: <TimesFlat sx={{fontSize: 16}} color="error"/>},
];

const RealizationsPage: React.FC = () => {
  const {pledgeId} = useParams<{ pledgeId: string }>();

  const [{ search, order_direction, page, date_from, date_to, status }, setQuery] =
      useQueryParams({
        search: withDefault(StringParam, ''),
        page: withDefault(NumberParam, 1),
        order_direction: withDefault(StringParam, OrderDirections.DESC),
        status: withDefault(StringParam, ''),
        date_from: withDefault(DateParam, moment().toDate()),
        date_to: withDefault(DateParam, moment().toDate()),
      });

  const [trigger, { data: realizations, isLoading, isFetching, isError, isSuccess }] =
      useLazyGetRealizationsQuery();

  // const {
  //     data: realizations,
  //     isLoading,
  //     isFetching,
  //     isError,
  //     isSuccess,
  //     refetch,
  // } = useGetRealizationsQuery(
  //     {
  //         params: {
  //             search: search || null,
  //             order_direction,
  //             page,
  //             order_by: 'id',
  //             pledge_id: pledgeId ? +pledgeId : null,
  //             status,
  //             date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
  //             date_to: moment(date_to).format('YYYY-MM-DD'),
  //         },
  //     },
  //     {
  //         refetchOnMountOrArgChange: false,
  //     }
  // );

  const [hasSearched, setHasSearched] = useState(false);

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      order_by: 'id',
      pledge_id: pledgeId ? +pledgeId : null,
      status,
      date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
      date_to: date_to ? moment(date_to).format('YYYY-MM-DD') : null,
    },
  });


  const onSearch = () => {
    // сбрасываем страницу в URL и запускаем запрос
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  const [createModalOpened, setOpenedCreateModal] = useState(false);


  return (
      <>
        <AppBar title="Реализации"/>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="flex-start" spacing={3}>
              <Grid container spacing={2}>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={(event) => setQuery({search: event.target.value, page: 1})}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={order_direction}
                      label="Сортировать по"
                      disabled={isLoading}
                      onChange={(event) => setQuery({order_direction: event.target.value})}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({value, label}) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={status}
                      label="Статус"
                      disabled={isLoading}
                      onChange={(event) => setQuery({status: event.target.value})}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>

                    {REALIZATION_STATUSES.map(({value, label}) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={(value) => setQuery({date_from: value ? moment(value).toDate() : undefined})}
                      disabled={isLoading || isFetching}
                      renderInput={(params) => (
                          <TextField {...params} name="date_from" variant="filled" size="small"
                                     fullWidth/>
                      )}
                  />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={(value) => setQuery({date_to: value ? moment(value).toDate() : undefined})}
                      disabled={isLoading || isFetching}
                      renderInput={(params) => (
                          <TextField {...params} name="date_to" variant="filled" size="small" fullWidth/>
                      )}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <Button
                      disabled={isLoading || isFetching}
                      size="large"
                      variant="contained"
                      fullWidth
                      onClick={onSearch}
                  >
                    Поиск
                  </Button>

                </Grid>
              </Grid>

              <Box sx={{width: 210}}>
                <Button
                    disabled={isLoading}
                    onClick={() => setOpenedCreateModal(true)}
                    size="large"
                    variant="contained"
                    fullWidth
                >
                  Добавить
                </Button>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {(!hasSearched && <Empty text="Нажмите «Поиск», чтобы увидеть данные" />) ||
                (isLoading ? (
                    <Loader />
                ) : isError ? (
                    <Error text="Произошла ошибка с загрузкой реализаций! Пожалуйста, попробуйте позже" />
                ) : isSuccess && realizations?.data?.length ? (
                    <RealizationsTable
                        list={realizations}
                        page={page}
                        disabled={isFetching}
                        onChangePage={onChangePage}
                    />
                ) : (
                    <Empty text="Реализации отсутствуют" />
                ))}
          </Grid>
        </Grid>

        <CreateRealization onClose={() => setOpenedCreateModal(false)} open={createModalOpened}/>
      </>
  );
};

export default RealizationsPage;
